import React from 'react';
import { KbdProps } from '../../../types/DesignSystem/Kbd';
import { withClassName } from '../cn';

/**
 * Kbd (§ 247) — the keycap.
 *
 * The design system had no way to SHOW a keyboard shortcut, so no menu row in the
 * product advertised one — and shortcuts nobody can discover are shortcuts nobody
 * uses. This is the smallest honest keycap: a quiet ground under a hairline edge,
 * with a 2px bottom edge for the cap's depth (a flat chip reads as a tag; the heavier
 * base is what says "key"). Caption size, secondary ink, tabular numerals so `1`–`9`
 * caps stay one width; `select-none`, because a shortcut hint is chrome, not copy.
 *
 * § 295 — the ground is `surface-hover-strong`, not `surface-sunken`. In light the two
 * are one hex, so nothing moved. In dark `surface-sunken` is the PAGE ground: a keycap
 * inside a raised menu (its usual home, via `MenuItem.shortcut`) measured 1.21:1
 * DARKER than the row around it and read as a hole punched through the menu. A key is
 * a raised thing; `hover-strong` lifts one rung off the raised surface in dark — the
 * § 256 move, made for the same reason.
 *
 * Sans, not the mono the UA stylesheet gives `<kbd>`: a keycap sits inside a menu row
 * beside a sans label, and switching face mid-row for two characters reads as a
 * rendering fault, not a convention (§ 196's single-family rule).
 */
/**
 * § 303 — the glyph map. The ARIA `aria-keyshortcuts` vocabulary is the INPUT
 * (`Meta`, `Control`, `Shift`, `ArrowUp` …); what a cap PRINTS is the platform's
 * own spelling of it. Before this, `Search shortcut="Meta+K"` and every Menu row
 * rendered the literal word "Meta" — a name no keyboard carries. Anything not in
 * the map (letters, digits, `F5`, `/`) prints as it is.
 */
type ResolvedPlatform = 'mac' | 'other';

const GLYPHS: Record<ResolvedPlatform, Record<string, string>> = {
  mac: {
    Meta: '⌘', Control: '⌃', Alt: '⌥', Shift: '⇧',
    Enter: '↵', Escape: 'Esc', Backspace: '⌫', Delete: '⌦', Tab: '⇥',
    ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', ' ': 'Space', Space: 'Space',
  },
  other: {
    /* `Meta` prints Ctrl: it is the primary modifier a Windows or Linux user holds for
       the chord a mac user holds ⌘ for. The ARIA string keeps saying Meta — the
       caller still owns which spelling is right for the platform they bind on. */
    Meta: 'Ctrl', Control: 'Ctrl', Alt: 'Alt', Shift: 'Shift',
    Enter: '↵', Escape: 'Esc', Backspace: '⌫', Delete: 'Del', Tab: 'Tab',
    ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', ' ': 'Space', Space: 'Space',
  },
};

/** Read once per module: the platform does not change under a running page. */
export const detectPlatform = (): ResolvedPlatform => {
  if (typeof navigator === 'undefined') return 'other';
  const signature = `${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`;
  return /Mac|iPhone|iPad|iPod/.test(signature) ? 'mac' : 'other';
};

/** The printed form of one ARIA key name on a platform. Unknown keys print as they are. */
export const glyphFor = (key: string, platform: ResolvedPlatform): string => GLYPHS[platform][key] ?? key;

const Kbd = React.forwardRef<HTMLElement, KbdProps>(function Kbd({
  children,
  platform = 'auto',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const resolved: ResolvedPlatform = platform === 'auto' ? detectPlatform() : platform;
  /* Only a STRING child is a key name; a node is the caller's own drawing. */
  const isKey = typeof children === 'string';
  return (
    <kbd
      /* rest first — the Button.tsx rule. */
      {...rest}
      ref={ref}
      data-testid={dataTestId}
      /* The ARIA spelling, kept on the element so a spec can assert the KEY while the
         cap prints the glyph — `[data-key="Meta"]` finds the ⌘ and the Ctrl alike. */
      data-key={isKey ? children : undefined}
      className={withClassName(`tw-inline-flex tw-items-center tw-justify-center tw-min-w-5 tw-h-5 tw-px-1.5 tw-rounded-ctl tw-border tw-border-edge tw-border-b-2 tw-bg-surface-hover-strong tw-text-ink-secondary tw-text-caption-metadata tw-font-noto-sans tw-tabular-nums tw-select-none tw-align-middle tw-whitespace-nowrap`, className)}
    >
      {isKey ? glyphFor(children, resolved) : children}
    </kbd>
  );
});

/**
 * `'Control+Shift+P'` → `['Control', 'Shift', 'P']`. The ARIA `aria-keyshortcuts`
 * spelling is the input (keys joined by `+`, spaces tolerated), so the string a row
 * DISPLAYS is the string assistive technology is told — one source. A lone `'+'` is
 * the plus key itself, not an empty combination.
 */
export const keysOf = (shortcut: string): string[] => {
  const trimmed = shortcut.trim();
  if (trimmed === '+') return ['+'];
  return trimmed.split('+').map((key) => key.trim()).filter(Boolean);
};

export default Kbd;
